import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { useSEO } from '../hooks/useSEO';

/**
 * Shared layout for all blog posts. Handles SEO meta, header, footer,
 * article schema, and CTA back to the product page.
 */
export const BlogPostLayout = ({
  title,
  description,
  slug,
  category,
  readTime,
  datePublished,
  heroImage,
  heroImageAlt,
  children
}) => {
  const canonical = `https://curveeforyou.com/blog/${slug}`;

  useSEO({
    title: `${title} | CurveeForYou Blog`,
    description,
    canonical
  });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    ...(heroImage ? { image: [heroImage] } : {}),
    author: { '@type': 'Organization', name: 'CurveeForYou' },
    publisher: {
      '@type': 'Organization',
      name: 'CurveeForYou',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png'
      }
    },
    datePublished,
    dateModified: datePublished,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/30 to-white">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" data-testid="blog-post-nav-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_item-landing/artifacts/feqbyn90_Logo%20curveeforyou.png"
                alt="CurveeForYou"
                className="h-20 md:h-24 w-auto"
              />
            </Link>
            <div className="flex gap-2">
              <Link to="/blog">
                <Button variant="outline" className="border-[#7d1f3d] text-[#7d1f3d] hover:bg-[#7d1f3d] hover:text-white" data-testid="blog-post-all-articles-button">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  All Articles
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
          <span className="inline-flex items-center gap-1 bg-rose-100 text-[#7d1f3d] px-3 py-1 rounded-full font-semibold">
            <Tag className="w-3 h-3" />
            {category}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {readTime}
          </span>
        </div>

        <h1 className="text-3xl md:text-5xl font-bold text-[#7d1f3d] mb-6 leading-tight" data-testid="blog-post-title">
          {title}
        </h1>

        <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed border-l-4 border-[#d4af37] pl-4 italic">
          {description}
        </p>

        {heroImage && (
          <div className="mb-10 rounded-2xl overflow-hidden bg-rose-50/40 aspect-[16/10] shadow-xl">
            <img
              src={heroImage}
              alt={heroImageAlt || title}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>
        )}

        <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed space-y-6 [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-bold [&_h2]:text-[#7d1f3d] [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-base [&_p]:md:text-lg [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_li]:text-base [&_li]:md:text-lg [&_strong]:text-[#7d1f3d] [&_a]:text-[#7d1f3d] [&_a]:font-semibold [&_a]:underline">
          {children}
        </div>

        {/* In-article CTA */}
        <div className="mt-16 p-8 bg-gradient-to-br from-[#7d1f3d] to-[#5d1729] text-white rounded-2xl shadow-2xl text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Ready to Get Snatched?</h3>
          <p className="text-rose-100 mb-6">
            Skip the trial and error. Get the medical-grade Faja Colombiana that&apos;s loved by 1,247+ women — at 25% off today.
          </p>
          <Link to="/">
            <Button className="bg-[#d4af37] hover:bg-[#c49d2a] text-[#5d1729] font-bold px-8 py-6 text-lg" data-testid="blog-post-cta-shop">
              Shop the Faja — $41.99
            </Button>
          </Link>
        </div>
      </article>

      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
    </div>
  );
};
